import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Save } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { purchaseService } from '../../services/purchaseService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import Field from '../../components/Field';
import { useColumnFilters, FilterPopover } from '../../components/gridFilters';
import { cell, GRID } from '../../styles/gridStyles';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { landedTotalBase, solveBasePrice, BASE_PRICE_COMPONENT, currencyOfComponent } from '../../utils/landedCost';
import { userPrefs, PREFS } from '../../utils/userPreferences';

// =============================================================================
// Monthly Pricing (Purchase) — the landed price of every stocked item, by month.
//
// Stocked Pricing builds a single month's price UP from its landed-cost
// components. This page is the other way round: pick a warehouse and product
// class, see the finished landed price for all 12 months, and type over any month
// you want to change. The edit is applied by BACK-SOLVING the base purchase price
// that produces the new landed total (every other component — freight, duty, VAT,
// handling — is held constant), and that base price is what gets written to
// bud_stock_pricing. See utils/landedCost.js for the algebra.
//
// Site comes from the top bar (it drives the warehouse list), the year is
// internal — the same arrangement as Stocked Pricing.
// =============================================================================

// Year is not surfaced in the UI. Kept internally because the item universe comes
// from act_stock_codes (Stock Actuals), which is still year-based.
const THIS_YEAR = new Date().getFullYear();

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MonthlyPricing = () => {
  const { selectedSite, selectedVersion, vatRate, fyStartMonth } = useAppContext();

  // Warehouse + product class are remembered per user and SHARED with Stocked
  // Pricing (utils/userPreferences.js), so switching between the two pricing
  // views keeps you on the same items. Warehouse is per site, so it can only be
  // restored once that site's list has loaded.
  const [warehouses, setWarehouses] = useState([]);
  const [warehouse, setWarehouse] = useState('');
  const [productClasses, setProductClasses] = useState([]);
  const [productClass, setProductClass] = useState(() => userPrefs.get(PREFS.PURCHASE_PRODUCT_CLASS, ''));
  const year = THIS_YEAR;

  const [items, setItems] = useState([]);       // [{ stock_code, description, unit_of_measure, periods: {p: {components, currencies}} }]
  const [currencies, setCurrencies] = useState([]);
  const [edits, setEdits] = useState({});       // { `${stock}:${p}`: targetLanded } — typed landed values
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  // ---- Pickers -------------------------------------------------------------
  useEffect(() => {
    purchaseService.getProductClasses()
      .then((r) => { if (r.success) setProductClasses(r.data); })
      .catch(() => {});
  }, []);

  // Persist the pickers; drop a remembered class that no longer exists.
  useEffect(() => { userPrefs.set(PREFS.PURCHASE_PRODUCT_CLASS, productClass); }, [productClass]);
  useEffect(() => {
    if (productClass && productClasses.length && !productClasses.includes(productClass)) {
      setProductClass('');
    }
  }, [productClasses, productClass]);
  useEffect(() => {
    if (!selectedSite || !warehouse) return;
    userPrefs.set(PREFS.PURCHASE_WAREHOUSE, {
      ...(userPrefs.get(PREFS.PURCHASE_WAREHOUSE, {}) || {}), [selectedSite]: warehouse,
    });
  }, [selectedSite, warehouse]);

  // Currencies are per budget version (the version travels in the header).
  useEffect(() => {
    purchaseService.getCurrencies()
      .then((r) => { if (r.success) setCurrencies(r.data); })
      .catch(() => {});
  }, [selectedVersion]);

  // Cleared first so the grid can't load the old site's warehouse against the
  // new site; the remembered one is re-applied if this site still has it.
  useEffect(() => {
    setWarehouse('');
    setItems([]);
    if (!selectedSite) { setWarehouses([]); return undefined; }
    let cancelled = false;
    purchaseService.getWarehouses(selectedSite)
      .then((r) => {
        if (cancelled) return;
        const list = r.success ? r.data : [];
        setWarehouses(list);
        const saved = (userPrefs.get(PREFS.PURCHASE_WAREHOUSE, {}) || {})[selectedSite];
        if (saved && list.some((w) => String(w.warehouse_id) === String(saved))) setWarehouse(saved);
      })
      .catch(() => { if (!cancelled) setWarehouses([]); });
    return () => { cancelled = true; };
  }, [selectedSite]);

  // ---- Pricing context (currency rates + VAT) ------------------------------
  const baseCode = useMemo(
    () => currencies.find((c) => c.is_base)?.currency_code || null, [currencies]);
  const rateOf = useCallback((code) => {
    const c = currencies.find((x) => x.currency_code === code);
    return c ? Number(c.conversion_to_base) : 1;
  }, [currencies]);
  const ctx = useMemo(() => ({ vatRate, rateOf, baseCode }), [vatRate, rateOf, baseCode]);

  // ---- Load ----------------------------------------------------------------
  const loadItems = useCallback(async () => {
    if (!warehouse) { setItems([]); return; }
    setIsLoading(true);
    setError(null);
    try {
      const r = await purchaseService.getStockedMonthly({
        warehouse, year, productClass: productClass || undefined,
      });
      setItems(r.success ? r.data.items : []);
      setEdits({});
      setWarning(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load monthly pricing.');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [warehouse, year, productClass]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---- Values --------------------------------------------------------------
  const cellOf = useCallback(
    (item, p) => item.periods?.[p] || { components: {}, currencies: {} }, []);

  // The landed price shown for an item-month: the typed value if edited, else
  // derived from the stored components.
  const landedOf = useCallback((item, p) => {
    const k = `${item.stock_code}:${p}`;
    if (edits[k] !== undefined) return Number(edits[k]);
    return landedTotalBase(cellOf(item, p), ctx);
  }, [edits, cellOf, ctx]);

  // ---- Stock Code column filter (header ▾) ---------------------------------
  const accessors = useMemo(() => ({ item: (it) => it.stock_code || '' }), []);
  const { filters, setFilter, clearAll, filtered, activeIds } = useColumnFilters(items, accessors);
  const [filterUI, setFilterUI] = useState(null);

  const onHeaderMenu = useCallback((columnId, bounds) => {
    const acc = accessors[columnId];
    if (!acc) return;
    const options = [...new Set(items.map((it) => String(acc(it) ?? '')))].sort();
    setFilterUI({ colId: columnId, options, x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 });
  }, [accessors, items]);

  // ---- Grid ----------------------------------------------------------------
  const columns = useMemo(() => ([
    { columnId: 'item', width: 300, resizable: true, hasMenu: true },
    { columnId: 'uom', width: 70, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 92, resizable: true })),
    { columnId: 'AVG', width: 110, resizable: true },
  ]), []);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header(activeIds.includes('item') ? 'Item / Stock Code ●' : 'Item / Stock Code'),
        cell.header('UoM'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header(`Avg${baseCode ? ` (${baseCode})` : ''}`, { align: 'right' }),
      ],
    };

    const dataRows = filtered.map((item, idx) => {
      const vals = PERIODS.map((p) => landedOf(item, p));
      const avg = vals.reduce((a, v) => a + v, 0) / (vals.length || 1);
      return {
        rowId: item.stock_code,
        height: GRID.size.dataRow,
        cells: [
          cell.label(`${item.stock_code} · ${item.description}`, { idx }),
          cell.text(item.unit_of_measure || '—', { idx }),
          ...PERIODS.map((p) => cell.number(landedOf(item, p), { editable: true, idx })),
          cell.aggregate(Math.round(avg * 100) / 100, { kind: 'year' }),
        ],
      };
    });

    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel(activeIds.length ? 'TOTAL (filtered)' : 'TOTAL'),
        cell.totalBlank(),
        ...PERIODS.map((p) => cell.totalNumber(filtered.reduce((a, it) => a + landedOf(it, p), 0))),
        cell.totalBlank(),
      ],
    };

    return [headerRow, ...dataRows, totalRow];
  }, [filtered, landedOf, fyStartMonth, baseCode, activeIds]);

  // ---- Edits ---------------------------------------------------------------
  const handleChanges = (changes) => {
    const touched = {};
    for (const ch of changes) {
      const col = String(ch.columnId);
      if (!col.startsWith('P') || ch.rowId === 'header' || ch.rowId === 'totals') continue;
      const p = Number(col.slice(1));
      if (!(p >= 1 && p <= 12)) continue;
      const val = Number(ch.newCell?.value);
      if (isNaN(val)) continue;
      touched[`${ch.rowId}:${p}`] = val;
    }
    if (Object.keys(touched).length) setEdits((prev) => ({ ...prev, ...touched }));
  };

  // ---- Save ----------------------------------------------------------------
  // Every edited month is converted into the base purchase price that produces
  // the typed landed total. Months where that is impossible (the other landed
  // components already cost more than the target) are reported and skipped.
  const handleSave = async () => {
    setError(null); setWarning(null);
    const byCode = Object.fromEntries(items.map((i) => [i.stock_code, i]));
    const changes = [];
    const unreachable = [];

    for (const [key, target] of Object.entries(edits)) {
      const sep = key.lastIndexOf(':');
      const stock_code = key.slice(0, sep);
      const p = Number(key.slice(sep + 1));
      const item = byCode[stock_code];
      if (!item) continue;
      const c = cellOf(item, p);
      const amount = solveBasePrice(c, target, ctx);
      if (amount === null) {
        unreachable.push(`${stock_code} · ${periodLabel(p, fyStartMonth)} (target ${fmt(target)})`);
        continue;
      }
      changes.push({
        stock_code,
        financial_period: p,
        component_code: BASE_PRICE_COMPONENT,
        amount,
        currency: currencyOfComponent(c, BASE_PRICE_COMPONENT, ctx),
      });
    }

    if (unreachable.length) {
      setWarning(
        `${unreachable.length} month(s) can't be reached by changing the base price alone — the other landed-cost ` +
        `components already cost more than the target. Lower those on Stocked Pricing first: ${unreachable.slice(0, 5).join('; ')}` +
        `${unreachable.length > 5 ? ` and ${unreachable.length - 5} more` : ''}.`
      );
    }
    if (changes.length === 0) return;

    setIsSaving(true);
    try {
      const res = await purchaseService.saveStockedMonthly({
        warehouse_id: warehouse, financial_year: year, changes,
      });
      if (!res.success) throw new Error(res.message || res.error);
      await loadItems();
      if (unreachable.length) {
        setWarning(`Saved ${changes.length} month(s). ${unreachable.length} could not be applied — see above.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save pricing.');
    } finally {
      setIsSaving(false);
    }
  };

  const editCount = Object.keys(edits).length;
  const hasChanges = editCount > 0;
  const selectClass =
    'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Title, badge and pickers share one row — same compact header as
          Stocked Pricing, so the grid keeps the height. */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <h1 className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100">
            Monthly Pricing
          </h1>
          <Field dense label="Warehouse">
            <select className={selectClass} value={warehouse} disabled={!selectedSite || warehouses.length === 0}
              onChange={(e) => setWarehouse(e.target.value)}>
              <option value="">{selectedSite ? 'Select warehouse' : 'Select a site first'}</option>
              {warehouses.map((w) => <option key={w.warehouse_id} value={w.warehouse_id}>{w.description}</option>)}
            </select>
          </Field>
          <Field dense label="Product Class">
            <select className={selectClass} value={productClass} onChange={(e) => setProductClass(e.target.value)}>
              <option value="">All classes</option>
              {productClasses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          {/* Stays visible: it is the one warning that editing a landed price
              rewrites the base price, not the component you are looking at. */}
          <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap border border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200">
            Changes to pricing adjust base pricing
          </span>
        </div>

        {error && <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md">{error}</div>}
        {warning && <div className="mt-2 p-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md">{warning}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-4">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!warehouse ? (
            <div className="p-10 text-center text-sm text-slate-400">
              {selectedSite
                ? 'Select a warehouse to see monthly pricing.'
                : 'Select a site in the top bar, then choose a warehouse.'}
            </div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onHeaderMenuClick={onHeaderMenu}
              hint={`Landed price per month${baseCode ? ` in ${baseCode}` : ''}. Typing a new price re-solves the base purchase price for that month — freight, duty, VAT and handling stay as they are.`}
              toolbar={
                <>
                  {activeIds.length > 0 && (
                    <MantleGridButton onClick={clearAll} accent>
                      Clear filters ({activeIds.length})
                    </MantleGridButton>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50 shadow-sm transition-colors ${
                      hasChanges ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
                    }`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? 'Saving…' : hasChanges ? `Save ${editCount} change${editCount === 1 ? '' : 's'}` : 'Save'}
                  </button>
                </>
              }
            />
          )}
        </div>
      </div>

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
};

export default MonthlyPricing;
